import classNames from 'classnames';
import Link from 'next/link';
import styles from './LightRetouchButton.module.scss';

export enum LightRetouchButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    TertiaryInverse = 'tertiaryInverse',
}

export enum LightRetouchButtonSize {
    /** Standard 48px control height used by most CTAs. */
    Default = 'default',
    /** No height is applied; the consumer sets `h-*` or padding itself. */
    Compact = 'compact',
}

interface LightRetouchButtonBaseProps {
    children: React.ReactNode;
    /** Extra class(es) for sizing, positioning, or any overrides. */
    className?: string;
    variant?: LightRetouchButtonVariant;
    size?: LightRetouchButtonSize;
}

type LightRetouchButtonLinkProps = LightRetouchButtonBaseProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
        href: string;
    };

type LightRetouchButtonProps =
    | (LightRetouchButtonBaseProps &
          React.ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined })
    | LightRetouchButtonLinkProps;

/**
 * Light Retouch button variants aligned with Figma (Primary, Secondary, Tertiary, TertiaryInverse).
 * Provides visual states (bg, hover overlay, focus ring, disabled) plus the standard
 * 48px height. Width, padding and positioning stay with the consumer via `className`.
 *
 * Pass `size={LightRetouchButtonSize.Compact}` for denser UI (modals, inline edit rows):
 * no height is emitted, so a plain `h-[38px]` in `className` wins without `!important`.
 *
 * - No `href` → renders as `<button>`
 * - With `href`  → renders as `<Link>`
 */
const LightRetouchButton: React.FC<LightRetouchButtonProps> = ({
    children,
    className,
    variant = LightRetouchButtonVariant.Primary,
    size = LightRetouchButtonSize.Default,
    href,
    ...props
}) => {
    const cls = classNames(
        styles.btn,
        styles[variant],
        { [styles.default]: size === LightRetouchButtonSize.Default },
        className,
    );

    if (href !== undefined) {
        return (
            <Link
                href={href}
                {...(props as Omit<LightRetouchButtonLinkProps, 'children' | 'className' | 'href'>)}
                className={cls}
            >
                {children}
            </Link>
        );
    }

    return (
        <button
            type="button"
            {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
            className={cls}
        >
            {children}
        </button>
    );
};

export default LightRetouchButton;
